import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/pages/Messages";
import { UserPlus, Copy, Trash2, Users, Link2 } from "lucide-react";

export default function Delegates() {
  const [data, setData] = useState({ delegates: [], pending_invites: [] });
  const [props, setProps] = useState([]);
  const [show, setShow] = useState(false);
  const [link, setLink] = useState("");

  const load = async () => {
    const [d, p] = await Promise.all([api.get("/delegates"), api.get("/properties")]);
    setData(d.data); setProps(p.data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => { await api.delete(`/delegates/${id}`); load(); toast.success("Collaboratore rimosso"); };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-bold tracking-tight">Collaboratori</h1><p className="text-muted-foreground text-sm mt-1">Invita responsabili delegati con permessi limitati</p></div>
        <button onClick={() => { setShow(true); setLink(""); }} className="bg-primary text-primary-foreground font-medium px-4 py-2.5 rounded-lg flex items-center gap-2" data-testid="delegate-invite-button"><UserPlus className="h-4 w-4" /> Invita</button>
      </div>

      <div className="bg-card rounded-xl border border-border/80 p-5">
        <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Team</h2>
        <div className="divide-y divide-border/60">
          {data.delegates.map((d) => (
            <div key={d.id} className="py-3 flex items-center justify-between">
              <div><div className="font-medium text-sm">{d.name}</div><div className="text-xs text-muted-foreground">{d.email} · <span className="capitalize">{d.role_label || "gestore"}</span></div></div>
              <button onClick={() => del(d.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {data.delegates.length === 0 && <p className="text-sm text-muted-foreground py-4">Nessun collaboratore ancora.</p>}
        </div>
        {data.pending_invites.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/60">
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Inviti in sospeso</p>
            {data.pending_invites.map((i) => <div key={i.id} className="text-sm py-1 text-muted-foreground flex items-center gap-2"><Link2 className="h-3.5 w-3.5" /> {i.email || "Invito aperto"}</div>)}
          </div>
        )}
      </div>

      {show && <InviteModal props={props} onClose={() => setShow(false)} link={link} setLink={setLink} />}
    </div>
  );
}

function InviteModal({ props, onClose, link, setLink }) {
  const PRESETS = { gestore: { wallet: true, company: true, support: true }, operatore: { wallet: false, company: false, support: true } };
  const [f, setF] = useState({ name: "", email: "", role_label: "gestore", services: ["homesmart"], wallet: true, company: true, support: true, assigned_property_ids: [] });
  const setRole = (r) => setF({ ...f, role_label: r, ...PRESETS[r] });
  const toggle = (id) => setF({ ...f, assigned_property_ids: f.assigned_property_ids.includes(id) ? f.assigned_property_ids.filter((x) => x !== id) : [...f.assigned_property_ids, id] });
  const create = async () => {
    const { data } = await api.post("/delegates/invite", f);
    setLink(data.invite_link); toast.success("Invito creato");
  };
  const Toggle = ({ k, label }) => (
    <label className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg border border-border">
      <span>{label}</span>
      <input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="h-4 w-4 accent-[#E3FF00]" data-testid={`perm-${k}`} />
    </label>
  );
  return (
    <Modal onClose={onClose} title="Invita collaboratore">
      {link ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Condividi questo link con il collaboratore:</p>
          <div className="flex gap-2"><input readOnly value={link} className="flex-1 bg-muted border border-input rounded-lg px-3 py-2 text-xs" /><button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copiato"); }} className="bg-[#E3FF00] text-neutral-900 px-3 rounded-lg"><Copy className="h-4 w-4" /></button></div>
        </div>
      ) : (
        <div className="space-y-3">
          <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nome" className="w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm" data-testid="delegate-name-input" />
          <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className="w-full bg-background border border-input rounded-lg px-3 py-2.5 text-sm" data-testid="delegate-email-input" />
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Ruolo</p>
            <div className="grid grid-cols-2 gap-2">
              {["gestore", "operatore"].map((r) => (
                <button key={r} onClick={() => setRole(r)} data-testid={`role-${r}`} className={`py-2 rounded-lg text-sm font-semibold capitalize border-2 transition-all ${f.role_label === r ? "border-neutral-900 bg-[#E3FF00] text-neutral-900" : "border-border text-muted-foreground"}`}>{r}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Permessi</p>
            <div className="space-y-2">
              <label className="flex items-center justify-between gap-2 text-sm p-2 rounded-lg border border-border">
                <span>Servizio Home Smart</span>
                <input type="checkbox" checked={f.services.includes("homesmart")} onChange={(e) => setF({ ...f, services: e.target.checked ? ["homesmart"] : [] })} className="h-4 w-4 accent-[#E3FF00]" data-testid="perm-homesmart" />
              </label>
              <Toggle k="wallet" label="Borsellino (vedi e ricarica)" />
              <Toggle k="company" label="Azienda (vedi e modifica)" />
              <Toggle k="support" label="Assistenza (apri segnalazioni)" />
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Immobili assegnati (Home Smart)</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {props.map((p) => <label key={p.id} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-muted cursor-pointer"><input type="checkbox" checked={f.assigned_property_ids.includes(p.id)} onChange={() => toggle(p.id)} className="accent-[#E3FF00]" /> {p.data?.name || "Immobile"}</label>)}
              {props.length === 0 && <p className="text-xs text-muted-foreground">Nessun immobile disponibile.</p>}
            </div>
          </div>
          <button onClick={create} className="w-full bg-[#E3FF00] text-neutral-900 font-semibold py-2.5 rounded-lg hover:brightness-95" data-testid="delegate-create-invite">Genera link d'invito</button>
        </div>
      )}
    </Modal>
  );
}
